/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetDirectoryReviewSettingsRequest,
  GetDirectoryReviewSettingsSecurity,
} from "../models/operations/getdirectoryreviewsettings.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDirectoryReviewSettingsQuery,
  DirectoryReviewSettingsQueryData,
  prefetchDirectoryReviewSettings,
  queryKeyDirectoryReviewSettings,
} from "./directoryReviewSettings.core.js";
export {
  buildDirectoryReviewSettingsQuery,
  type DirectoryReviewSettingsQueryData,
  prefetchDirectoryReviewSettings,
  queryKeyDirectoryReviewSettings,
};

export type DirectoryReviewSettingsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getDirectoryReviewSettings access
 *
 * @remarks
 * Get the organization's directory review settings: whether its admins are emailed when directory changes are waiting for a decision.
 */
export function useDirectoryReviewSettings(
  request?: GetDirectoryReviewSettingsRequest | undefined,
  security?: GetDirectoryReviewSettingsSecurity | undefined,
  options?: QueryHookOptions<
    DirectoryReviewSettingsQueryData,
    DirectoryReviewSettingsQueryError
  >,
): UseQueryResult<
  DirectoryReviewSettingsQueryData,
  DirectoryReviewSettingsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDirectoryReviewSettingsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getDirectoryReviewSettings access
 *
 * @remarks
 * Get the organization's directory review settings: whether its admins are emailed when directory changes are waiting for a decision.
 */
export function useDirectoryReviewSettingsSuspense(
  request?: GetDirectoryReviewSettingsRequest | undefined,
  security?: GetDirectoryReviewSettingsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DirectoryReviewSettingsQueryData,
    DirectoryReviewSettingsQueryError
  >,
): UseSuspenseQueryResult<
  DirectoryReviewSettingsQueryData,
  DirectoryReviewSettingsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDirectoryReviewSettingsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDirectoryReviewSettingsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DirectoryReviewSettingsQueryData,
): DirectoryReviewSettingsQueryData | undefined {
  const key = queryKeyDirectoryReviewSettings(...queryKeyBase);

  return client.setQueryData<DirectoryReviewSettingsQueryData>(key, data);
}

export function invalidateDirectoryReviewSettings(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "getDirectoryReviewSettings",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDirectoryReviewSettings(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "getDirectoryReviewSettings"],
  });
}
